import { isWrongResolved, readWrongProgress, saveCorrectReviews, wrongReviewSource, WrongReviewSource } from '../utils/wrongReviewProgress';
import CscaWrongPractice from '../components/CscaWrongPractice';
import HistoryRecordList from '../components/HistoryRecordList';
import HistoryScope from '../components/HistoryScope';
import WorkbookHistoryList from '../components/WorkbookHistoryList';
import { historyAccess, historyCategories, historyCategoryLabel, historyLevels, defaultHistoryLevel, HistoryCategory } from '../../../shared/historyScope';
import type { UserExamRestrictions } from '../utils/api';
import { isCscaPendingReview, getCscaSubjectName, isCscaSprintReview } from '../utils/cscaTestPresentation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fullSimulationResumePath } from '../utils/simulationResume';
import { useAuthStore, useTestStore } from '../store';
import { api, WritingGrading } from '../utils/api';
import { localApi } from '../utils/local-api';
import { buildCorrectSequence, isSequenceReviewQuestion, shouldUseLocalTestReview } from '../utils/sequenceQuestionScoring';
import { extractImagePath, stripImagePathFromText, resolveQuestionImagePath } from '../utils/simulationQuestionPresentation';
import {
  getHistoryScoreCardClass,
  getHistoryScorePanelClass,
  getHistoryScoreTextClass,
} from '../utils/historyScoreColors';
import { TestSession } from '../types';
import { Calendar, Clock, Trophy, CheckCircle, AlertCircle, HelpCircle, ChevronLeft, Volume2 } from 'lucide-react';
import PinyinText from '../components/PinyinText';
import MathText from '../components/MathText';
import CscaExplanationLanguageSwitch, { useCscaExplanationLanguage } from '../components/CscaExplanationLanguage';

type PracticeType = 'listening' | 'reading' | 'writing';
type PracticeFilterState = {
  version: string;
  level: string;
  subPart: string;
};

type WrongPracticeItem = {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  correctAnswers?: number[];
  reviewVersionKey?: string;
  media?: string | null;
  explanation?: string | null;
  explanationId?: string | null;
  answer?: number | null;
  answerContent?: any;
  choseBuzhidao?: boolean;
  completedAt: string;
  testId: string;
  testPart: string;
  practiceType: PracticeType;
  level: number;
  bankVersion: string;
  systemType: string;
  wrongReviewSource?: WrongReviewSource;
  subPart: string;
  questionGroup?: string | null;
  groupSeq?: number | null;
};

type HistoryCompositeCategory = 'listening' | 'reading' | 'writing';

type RecentLevelStats = {
  bankVersion: string;
  level: number;
  totalTests: number;
  listening: number;
  reading: number;
  writing: number;
  total: number;
};

const PRACTICE_TYPES: Array<{ part: PracticeType; label: string }> = [
  { part: 'listening', label: 'Listening Practice' },
  { part: 'reading', label: 'Reading Practice' },
  { part: 'writing', label: 'Writing Practice' },
];

const DEFAULT_PRACTICE_FILTERS: Record<PracticeType, PracticeFilterState> = {
  listening: { version: '', level: '', subPart: 'all' },
  reading: { version: '', level: '', subPart: 'all' },
  writing: { version: '', level: '', subPart: 'all' },
};

const DEFAULT_REVIEW_COUNTS: Record<PracticeType, number> = {
  listening: 20,
  reading: 20,
  writing: 20,
};

const inferPracticeType = (questionPart?: string | null, testPart?: string | null): PracticeType | null => {
  const normalizedQuestionPart = String(questionPart || '').trim().toUpperCase();
  if (normalizedQuestionPart.startsWith('L')) return 'listening';
  if (normalizedQuestionPart.startsWith('R')) return 'reading';
  if (normalizedQuestionPart.startsWith('W')) return 'writing';

  const normalizedTestPart = String(testPart || '').trim().toLowerCase();
  if (normalizedTestPart === 'listening') return 'listening';
  if (normalizedTestPart === 'reading') return 'reading';
  if (normalizedTestPart === 'writing') return 'writing';
  return null;
};

const normalizeSubPart = (questionPart?: string | null, fallbackPart?: string | null) => {
  const normalizedQuestionPart = String(questionPart || '').trim().toUpperCase();
  if (/^[LRW]\d+/.test(normalizedQuestionPart)) return normalizedQuestionPart;
  const inferredType = inferPracticeType(questionPart, fallbackPart);
  if (inferredType === 'listening') return 'LISTENING';
  if (inferredType === 'reading') return 'READING';
  if (inferredType === 'writing') return 'WRITING';
  return 'OTHER';
};

const getPracticePath = (part: PracticeType, level: number = 3) => {
  if (part === 'reading') return `/test/hsk${level}/reading`;
  if (part === 'writing') return `/test/hsk${level}/writing`;
  return `/test/hsk${level}/listening`;
};

const getPracticeLabel = (part: PracticeType) => {
  if (part === 'reading') return 'Reading';
  if (part === 'writing') return 'Writing';
  return 'Listening';
};

const formatHskTypeLabel = (value?: string | number | null) => {
  const normalized = String(value ?? '').trim();
  return normalized ? `HSK ${normalized}` : 'HSK';
};

const normalizeHskLevelValue = (value?: string | number | null) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > 0) return String(Math.trunc(numeric));
  return raw.replace(/\.0$/, '');
};

const formatHskLevelLabel = (value?: string | number | null) => {
  const normalized = normalizeHskLevelValue(value);
  return normalized ? `HSK${normalized}` : 'HSK';
};

const getHistoryFilterType = (part?: string | null, testId?: string | null, systemType?: string | null) => {
  const normalizedPart = String(part || '').trim().toLowerCase();
  const normalizedTestId = String(testId || '').trim().toLowerCase();
  if (systemType === 'topic_practice') return 'topic_practice';
  if (systemType === 'random_paper') return 'random_paper';
  if (systemType === 'mooc_topic_practice') return 'mooc_topic_practice';
  if (systemType === 'mooc_random') return 'mooc_random';
  if (normalizedTestId.startsWith('practice-')) return 'review';
  if (normalizedPart.startsWith('practice-')) return 'review';
  if (normalizedPart === 'listening') return 'listening';
  if (normalizedPart === 'reading') return 'reading';
  if (normalizedPart === 'writing') return 'writing';
  if (normalizedPart === 'simulation') return 'simulation';
	if (normalizedPart === 'math') return 'past_paper';
  return normalizedPart || 'other';
};

const getHistoryDisplayName = (bankVersion: string, level: number | string, part: string, count: number, testId?: string | null, systemType: string = 'simulation', workbookPart?: string | null, paperYear?: number | null, paperMonth?: number | null, topicCoarse?: string | null, topicFine?: string | null) => {
  const normalizedPart = String(part || '').trim().toLowerCase();
  const normalizedTestId = String(testId || '').trim().toLowerCase();
  const normalizedLevel = normalizeHskLevelValue(level);
	if (systemType === 'topic_practice') return `CSCA ${getCscaSubjectName(Number(level))} · ${topicFine ? `${topicCoarse} / ${topicFine}` : topicCoarse || '题型'} · 题型练习 ${count}`;
	if (systemType === 'random_paper') return `CSCA ${getCscaSubjectName(Number(level))} · 随机组卷 ${count}`;
	if (systemType === 'mooc_topic_practice') return `CSCA ${getCscaSubjectName(Number(level))} · MOOC · ${topicFine ? `${topicCoarse} / ${topicFine}` : topicCoarse || '题型'} · 分类练习 ${count}`;
	if (systemType === 'mooc_random') return `CSCA ${getCscaSubjectName(Number(level))} · 随机 MOOC ${count}`;
	if (bankVersion === 'CSCA' || systemType === 'past_paper') return `CSCA ${getCscaSubjectName(Number(level))} ${paperYear || ''}年${paperMonth || ''}月 · ${isCscaSprintReview({level:Number(level),systemType,paperYear,paperMonth}) ? '冲刺复习卷' : '往年试卷'} ${count}`;
  if (systemType === 'workbook') return `HSK ${bankVersion} ${normalizedLevel}级 Workbook${workbookPart ? ` · ${workbookPart}` : ''} ${count}`;
  if (normalizedTestId === 'practice-listening' || normalizedTestId.startsWith('practice-listening-')) return `HSK ${bankVersion} ${normalizedLevel}级 复习 听力 ${count}`;
  if (normalizedTestId === 'practice-reading' || normalizedTestId.startsWith('practice-reading-')) return `HSK ${bankVersion} ${normalizedLevel}级 复习 阅读 ${count}`;
  if (normalizedTestId === 'practice-writing' || normalizedTestId.startsWith('practice-writing-')) return `HSK ${bankVersion} ${normalizedLevel}级 复习 写作 ${count}`;
  if (normalizedPart === 'listening') return `HSK ${bankVersion} ${normalizedLevel}级 听力模拟 ${count}`;
  if (normalizedPart === 'reading') return `HSK ${bankVersion} ${normalizedLevel}级 阅读模拟 ${count}`;
  if (normalizedPart === 'writing') return `HSK ${bankVersion} ${normalizedLevel}级 写作模拟 ${count}`;
  if (normalizedPart === 'simulation') return `HSK ${bankVersion} ${normalizedLevel}级 模拟 ${count}`;
  if (normalizedPart === 'practice-listening') return `HSK ${bankVersion} ${normalizedLevel}级 复习 听力 ${count}`;
  if (normalizedPart === 'practice-reading') return `HSK ${bankVersion} ${normalizedLevel}级 复习 阅读 ${count}`;
  if (normalizedPart === 'practice-writing') return `HSK ${bankVersion} ${normalizedLevel}级 复习 写作 ${count}`;
  return `HSK ${bankVersion} ${normalizedLevel}级 ${part} ${count}`;
};

const getHistoryQuestionWeight = (part: string, level?: number | string | null) => {
  const normalizedPart = String(part || '').toUpperCase().trim();
  const normalizedLevel = Number(level || 0);

  if (normalizedLevel === 4) {
    const hsk4Weights: Record<string, number> = {
      L1: 2,
      L2: 2,
      L3: 2,
      L4: 3,
      R1: 2,
      R2: 2,
      R3: 2,
      R4: 3,
      R5: 3,
      W1: 6,
      W2: 8,
    };
    if (hsk4Weights[normalizedPart] != null) return hsk4Weights[normalizedPart];
  }

  if (normalizedLevel === 2) {
    if (normalizedPart === 'L1') return 2;
    if (normalizedPart === 'L2') return 3;
    if (normalizedPart === 'L3') return 3;
    if (normalizedPart === 'L4') return 4;
    if (normalizedPart === 'R1') return 3;
    if (normalizedPart === 'R2') return 4;
    if (normalizedPart === 'R3') return 5;
    if (normalizedPart === 'R4') return 4;
    return 1;
  }

  if (['L1', 'L2', 'R2'].some((prefix) => normalizedPart.startsWith(prefix))) return 2;
  if (['L3', 'L4', 'R1'].some((prefix) => normalizedPart.startsWith(prefix))) return 3;
  if (['R3', 'R4', 'R5'].some((prefix) => normalizedPart.startsWith(prefix))) return 4;
  if (normalizedPart === 'W1') return 12;
  if (normalizedPart === 'W2') return 8;
  return 1;
};

const getHistoryCompositeCategory = (part: string): HistoryCompositeCategory | null => {
  const normalizedPart = String(part || '').toLowerCase().trim();
  if (normalizedPart.startsWith('l') || normalizedPart === 'listening') return 'listening';
  if (normalizedPart.startsWith('r') || normalizedPart === 'reading') return 'reading';
  if (normalizedPart.startsWith('w') || normalizedPart === 'writing') return 'writing';
  return null;
};

const calculateRecentLevelStats = (level: number, tests: Array<{ sectionScores?: Record<string, number>; sectionMaxScores?: Record<string, number> }>): RecentLevelStats => {
  const categoryTotals: Record<HistoryCompositeCategory, { sum: number; count: number }> = {
    listening: { sum: 0, count: 0 },
    reading: { sum: 0, count: 0 },
    writing: { sum: 0, count: 0 },
  };

  tests.forEach((test) => {
    (['listening', 'reading', 'writing'] as HistoryCompositeCategory[]).forEach((category) => {
      const max = Number(test.sectionMaxScores?.[category] || 0);
      if (max <= 0) return;
      const score = Number(test.sectionScores?.[category] || 0);
      categoryTotals[category].sum += (score / max) * 100;
      categoryTotals[category].count += 1;
    });
  });

  const listening = categoryTotals.listening.count > 0 ? categoryTotals.listening.sum / categoryTotals.listening.count : 0;
  const reading = categoryTotals.reading.count > 0 ? categoryTotals.reading.sum / categoryTotals.reading.count : 0;
  const writing = categoryTotals.writing.count > 0 ? categoryTotals.writing.sum / categoryTotals.writing.count : 0;

  return {
    bankVersion: '',
    level,
    totalTests: tests.length,
    listening,
    reading,
    writing,
    total: listening + reading + writing,
  };
};

const TestHistory: React.FC = () => {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const { startTest, testHistory } = useTestStore();
  const [serverHistory, setServerHistory] = useState<any[]>([]);
  const historyCache = useRef(new Map<string, { at: number; data: any }>());
  const [shownHistory, setShownHistory] = useState({ category: 'simulation' as HistoryCategory, page: 1 });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [recentLevelStats, setRecentLevelStats] = useState<RecentLevelStats | null>(null);
  const [selectedStatsVersion, setSelectedStatsVersion] = useState<string>('');
  const [selectedStatsLevel, setSelectedStatsLevel] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyRetry, setHistoryRetry] = useState(0);
  
  // Detail View State
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [detailSession, setDetailSession] = useState<TestSession | null>(null);
  const { language: explanationLanguage, changeLanguage: changeExplanationLanguage } = useCscaExplanationLanguage();
  const [detailBankVersion, setDetailBankVersion] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [writingGradings, setWritingGradings] = useState<Record<string, WritingGrading>>({});
  const [writingGradingLoading, setWritingGradingLoading] = useState<Record<string, boolean>>({});
  const [writingGradingError, setWritingGradingError] = useState<Record<string, string>>({});

  const [wrongList, setWrongList] = useState<WrongPracticeItem[]>([]);
  const [wrongHistoryLoaded, setWrongHistoryLoaded] = useState(false);
  const [wrongHistoryLoading, setWrongHistoryLoading] = useState(false);
  const [wrongHistoryError, setWrongHistoryError] = useState('');
  const [reviewExpanded, setReviewExpanded] = useState(false);

  const [filterPart, setFilterPart] = useState<string>('all');
  const [filterVersion, setFilterVersion] = useState<string>(''); // 'all', '2.0', '3.0'
  const [filterLevel, setFilterLevel] = useState<string>('all'); // 'all', '1', '2', '3', '4', '5', '6'
  const [filterSystem, setFilterSystem] = useState<string>('all');
  const [restrictions, setRestrictions] = useState<UserExamRestrictions | null>(null);
  const [category, setCategory] = useState<HistoryCategory>('simulation');
  const [practiceFilters, setPracticeFilters] = useState<Record<PracticeType, PracticeFilterState>>(DEFAULT_PRACTICE_FILTERS);
  const [reviewCounts, setReviewCounts] = useState<Record<PracticeType, number>>(DEFAULT_REVIEW_COUNTS);

  useEffect(() => {
    let active = true;
    if (!token) return;
    api.getUserRestrictions(token).then(data => {
      if (!active) return;
      setRestrictions(data);
      const family = historyAccess(data).families[0] || '';
      setFilterVersion(family); setCategory(historyCategories(family)[0]); setFilterLevel(defaultHistoryLevel(data, family));
      if (!family) setLoading(false);
    }).catch(e => { if (active) { setError(e.message); setLoading(false); } });
    return () => { active = false; };
  }, [token]);
  const changeScope = (family: string, nextCategory: HistoryCategory, level: string) => {
    setFilterVersion(family); setCategory(nextCategory); setFilterLevel(level);
    setFilterPart('all'); setFilterSystem('all'); setHistoryPage(1);
  };

  // BACKEND_URL is now defined in vite.config.ts globally
  const API_BASE_URL = BACKEND_URL;
  const resolveMediaUrl = (media?: string | null) => {
    if (!media) return '';
    if (/^https?:\/\//i.test(media)) return media;
    return `${API_BASE_URL}/media/${String(media).replace(/^\/+/, '')}`;
  };
  const isAiScoredWritingPrompt = (q: any) =>
    Number(q?.level ?? detailSession?.level ?? 0) === 4 && String(q?.part || '').toUpperCase() === 'W2';
  const getReviewGradingKey = (sessionId: string, idx: number) => `${sessionId}:${idx}`;
  const getStoredWritingAnswer = (item: any) => {
    const content = item?.answer_content;
    if (Array.isArray(content)) {
      return { sequence: content, isCorrect: !!item?.correct };
    }
    if (content && typeof content === 'object') {
      return {
        sequence: Array.isArray(content.sequence) ? content.sequence : undefined,
        text: typeof content.text === 'string' ? content.text : undefined,
        isCorrect: !!item?.correct,
        aiScore: typeof content.ai_score === 'number' ? content.ai_score : null,
        awardedScore: typeof item?.awarded_score === 'number'
          ? item.awarded_score
          : (typeof content.awarded_score === 'number' ? content.awarded_score : null),
        maxScore: typeof content.max_score === 'number' ? content.max_score : null,
        grading: content.ai_grading || null,
      };
    }
    if (typeof content === 'string' && content.trim()) {
      return { text: content, isCorrect: !!item?.correct };
    }
    return null;
  };

  const handleGradeReviewWriting = async (sessionId: string, idx: number, prompt: string, answer: string) => {
    if (!token) return;
    const key = getReviewGradingKey(sessionId, idx);
    if (writingGradingLoading[key]) return;
    setWritingGradingError(prev => ({ ...prev, [key]: '' }));
    setWritingGradingLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await api.gradeWriting({ prompt, answer }, token);
      setWritingGradings(prev => ({ ...prev, [key]: res.grading }));
    } catch (err: any) {
      setWritingGradingError(prev => ({ ...prev, [key]: err?.message || 'AI 批改失败，请稍后重试。' }));
    } finally {
      setWritingGradingLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    let active = true;
    const loadHistory = async () => {
      if (!token || !restrictions || !filterVersion) return;
      if (filterSystem === 'review') { setLoading(false); return; }
      if (filterVersion === '3.0' && category === 'practice' && filterSystem === 'all') { setLoading(false); return; }
      
      try {
        setLoading(true);
        setError('');
        const key = JSON.stringify([token, filterVersion, category, filterLevel, filterSystem, filterPart, historyPage]);
        const cached = historyCache.current.get(key);
        const res = cached && Date.now() - cached.at < 30000 ? cached.data : await localApi.getHistoryPage(token, {
          page: historyPage,
          limit: 5,
          bankVersion: filterVersion,
          level: filterLevel,
          systemType: filterSystem,
          filterType: filterPart,
          category,
        });
        if (!active) return;
        historyCache.current.set(key, { at: Date.now(), data: res });
        if (historyCache.current.size > 30) historyCache.current.delete(historyCache.current.keys().next().value!);
        setShownHistory({ category, page: historyPage });
        const partCounter: Record<string, number> = {};
        
        const getCompletedAt = (test: any) => test.completed_at || test.date_taken || test.date;
        // Sort by completion date ascending to assign stable simulation numbers.
        const sortedAsc = (Array.isArray(res.history) ? res.history : []).sort((a: any, b: any) =>
          new Date(getCompletedAt(a)).getTime() - new Date(getCompletedAt(b)).getTime()
        );

        const mapped = sortedAsc.map((t: any) => {
          const part = String(t.part || '');
          const level = t.level;
          const bankVersion = t.bank_version || '2.0';
          const systemType = String(t.system_type || 'simulation');
          const workbookPart = t.workbook_part || null;
		  const paperYear = t.paper_year == null ? null : Number(t.paper_year);
		  const paperMonth = t.paper_month == null ? null : Number(t.paper_month);
		  const paperId = t.paper_id || null;
		  const topicCoarse = t.topic_coarse || null;
		  const topicFine = t.topic_fine || null;
          
          // Unique key for each test type combination: e.g. "3.0-2-listening"
          const counterKey = `${bankVersion}-${systemType}-${level}-${part}-${workbookPart || ''}-${paperId || ''}-${topicCoarse || ''}-${topicFine || ''}`;
          
          const count = (partCounter[counterKey] ?? 0) + 1;
          partCounter[counterKey] = count;
          
          // Ensure weighted score is used if available from backend (it should be from our recent backend update)
          // If not, calculate it client-side as fallback? No, let's rely on backend or client-side calc if missing
          // The backend returns 'weighted_score' and 'max_score' now.
          
          const weightedScore = t.weighted_score ?? t.score;
          const maxScore = bankVersion === 'CSCA' ? 100 : (t.max_score ?? (t.total_questions ? t.total_questions : 100));
          
          return {
            id: t.id,
            status: t.status || 'completed',
            progressCount: Number(t.progress_count || 0),
            lastActive: t.last_active,
            name: getHistoryDisplayName(bankVersion, level, part, count, t.id, systemType, workbookPart, paperYear, paperMonth, topicCoarse, topicFine).replace(/ \d+$/, ""),
            level: t.level,
            part: t.part,
            filterType: getHistoryFilterType(part, t.id, systemType),
            bankVersion: bankVersion,
            systemType,
            workbookPart,
			paperId,
			paperYear,
			paperMonth,
			topicCoarse,
			topicFine,
			sectionScores: t.section_scores || {},
			sectionMaxScores: t.section_max_scores || {},
			elapsedSeconds: Number(t.elapsed_seconds || 0),
            completedAt: getCompletedAt(t),
            finishedAt: t.completed_at || t.date_taken || t.date || null,
            totalQuestions: t.total_questions ?? t.totalQuestions,
            score: t.score, // Raw correct count
            weightedScore: weightedScore, 
            maxScore: maxScore,
            percentage: maxScore ? Math.round((weightedScore / maxScore) * 100) : 0,
            isReviewRecord: false,
          };
        });
        
        // Reverse back to descending (Newest first) for display
        const descendingHistory = [...mapped].reverse();
        setServerHistory(descendingHistory);
        setHistoryTotal(Number(res.pagination?.total || 0));
        setHistoryTotalPages(Number(res.pagination?.total_pages || 1));

        const latestHistoryItem = descendingHistory.find((item) => item.bankVersion !== 'CSCA');
        if (latestHistoryItem) {
          setSelectedStatsVersion(String(latestHistoryItem.bankVersion || '2.0'));
          setSelectedStatsLevel(normalizeHskLevelValue(latestHistoryItem.level));
        } else {
          setRecentLevelStats(null);
          setSelectedStatsVersion('');
          setSelectedStatsLevel('');
        }

      } catch (err: any) {
        if (active) { setError(err.message || 'Failed to load test history'); setServerHistory([]); }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadHistory();
    return () => { active = false; };
  }, [filterLevel, filterPart, filterSystem, filterVersion, historyPage, token, category, restrictions, historyRetry]);

  const loadWrongHistory = async () => {
    if (!token || wrongHistoryLoading || wrongHistoryLoaded) return;
    setWrongHistoryLoading(true);
    setWrongHistoryError('');
    try {
      const wrongRes = await api.getWrongHistory(token);
      const wrongPool: WrongPracticeItem[] = [];
      if (wrongRes.success && wrongRes.history) {
        for (const test of wrongRes.history) {
          const testPart = String(test.part || '');
          const level = Number(test.level || 0);
          const bankVersion = String((test as any).bank_version || '2.0');
          if (!restrictions || !historyAccess(restrictions).families.includes(bankVersion as any) || !historyLevels(restrictions, bankVersion).includes(level)) continue;
          for (const it of test.items || []) {
            const questionPart = String(it.part || '');
            const practiceType = bankVersion === 'CSCA' ? 'reading' : inferPracticeType(questionPart, testPart);
            if (!practiceType) continue;
            wrongPool.push({
              id: it.id,
              question: it.question,
              options: Array.isArray(it.options) ? it.options : [],
              correctAnswer: it.correctAnswer,
                correctAnswers: it.correctAnswers,
              reviewVersionKey: it.reviewVersionKey,
              media: it.media,
              explanation: it.explanation,
              explanationId: it.explanation_id,
              answer: it.answer,
              answerContent: it.answerContent,
              choseBuzhidao: !!it.chose_buzhidao,
              completedAt: test.date,
              testId: String(test.id),
              testPart,
              practiceType,
              level,
              bankVersion,
              systemType: String((test as any).system_type || 'simulation'),
              subPart: bankVersion === 'CSCA' ? String(it.coarse_type || '未分类') : normalizeSubPart(questionPart, testPart),
              questionGroup: it.question_group ?? null,
              groupSeq: it.group_seq ?? null,
            });
          }
        }
      }
      const progress = readWrongProgress(user?.id || '');
      setWrongList(wrongPool.map(item => ({ ...item, wrongReviewSource: wrongReviewSource(user?.id || '', item, wrongPool) })).filter(item => !isWrongResolved(item, progress)));
      setWrongHistoryLoaded(true);
    } catch (err: any) {
      setWrongHistoryError(err?.message || 'Failed to load wrong questions');
    } finally {
      setWrongHistoryLoading(false);
    }
  };

  const filteredWrongByPractice = useMemo(() => {
    return PRACTICE_TYPES.reduce((acc, { part }) => {
      const currentFilters = practiceFilters[part];
      acc[part] = wrongList.filter((item) => {
        if (item.practiceType !== part) return false;
        if (item.bankVersion !== filterVersion) return false;
        if (currentFilters.level && normalizeHskLevelValue(item.level) !== currentFilters.level) return false;
        if (currentFilters.subPart !== 'all' && item.subPart !== currentFilters.subPart) return false;
        return true;
      });
      return acc;
    }, {} as Record<PracticeType, WrongPracticeItem[]>);
  }, [practiceFilters, wrongList, filterVersion]);

  const localReviewHistory = useMemo(() => {
    const reviewCounter: Record<string, number> = {};

    return [...(testHistory || [])]
      .filter((session) => String(session.id || '').startsWith('practice-'))
      .sort((a, b) => new Date(a.completedAt || a.createdAt).getTime() - new Date(b.completedAt || b.createdAt).getTime())
      .map((session) => {
        const bankVersion = String((session.questions?.[0] as any)?.bankVersion || '2.0');
        const reviewKind = String(session.id || '').replace(/-\d+$/, '');
        const counterKey = `${bankVersion}-${session.level}-${reviewKind}`;
        const count = (reviewCounter[counterKey] ?? 0) + 1;
        reviewCounter[counterKey] = count;

        return {
          id: session.id,
          name: getHistoryDisplayName(bankVersion, session.level, session.part, count, session.id),
          level: session.level,
          part: session.part,
          filterType: getHistoryFilterType(session.part, session.id),
          bankVersion,
          systemType: 'review',
          workbookPart: null,
          completedAt: session.completedAt || session.createdAt,
          totalQuestions: session.questions.length,
          score: null,
          weightedScore: null,
          maxScore: null,
          percentage: null,
          isReviewRecord: true,
        };
      })
      .reverse();
  }, [testHistory]);

  const isLocalReviewFilter = filterSystem === 'review' || filterPart === 'review';
  const filteredLocalReviewHistory = useMemo(() => localReviewHistory.filter((test) => {
    if (!restrictions || !historyLevels(restrictions, filterVersion).includes(Number(test.level))) return false;
    if (filterVersion !== 'all' && String(test.bankVersion) !== filterVersion) return false;
    if (filterLevel !== 'all' && String(test.level) !== filterLevel) return false;
    return true;
  }), [filterLevel, filterVersion, localReviewHistory, restrictions]);
  const historyItems = useMemo(() => {
    if (!isLocalReviewFilter) return serverHistory;
    const offset = (historyPage - 1) * 5;
    return filteredLocalReviewHistory.slice(offset, offset + 5);
  }, [filteredLocalReviewHistory, historyPage, isLocalReviewFilter, serverHistory]);
  const displayedHistoryTotal = isLocalReviewFilter ? filteredLocalReviewHistory.length : historyTotal;
  const displayedHistoryTotalPages = isLocalReviewFilter
    ? Math.max(1, Math.ceil(filteredLocalReviewHistory.length / 5))
    : historyTotalPages;

  const availableStatsVersions = useMemo(() => {
    return Array.from(new Set(serverHistory.map((item) => String(item.bankVersion)).filter((version) => version && version !== 'CSCA'))).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
  }, [serverHistory]);

  const availableStatsLevels = useMemo(() => {
    if (!selectedStatsVersion) return [];
    return Array.from(
      new Set(
        serverHistory
          .filter((item) => String(item.bankVersion) === selectedStatsVersion)
          .map((item) => normalizeHskLevelValue(item.level))
          .filter(Boolean)
      )
    ).sort((a, b) => Number(a) - Number(b));
  }, [selectedStatsVersion, serverHistory]);

  const availableLevelsByPractice = useMemo(() => {
    return PRACTICE_TYPES.reduce((acc, { part }) => {
      const currentVersion = filterVersion;
      acc[part] = Array.from(
        new Set(
          wrongList
            .filter((item) => item.practiceType === part)
            .filter((item) => !currentVersion || item.bankVersion === currentVersion)
            .map((item) => normalizeHskLevelValue(item.level))
            .filter(Boolean)
        )
      ).sort((a, b) => Number(a) - Number(b));
      return acc;
    }, {} as Record<PracticeType, string[]>);
  }, [wrongList, filterVersion]);

  const availableSubPartsByPractice = useMemo(() => {
    return PRACTICE_TYPES.reduce((acc, { part }) => {
      acc[part] = Array.from(new Set(wrongList.filter(item => item.practiceType === part && item.bankVersion === filterVersion && (!practiceFilters[part].level || String(item.level) === practiceFilters[part].level)).map(item => item.subPart))).sort();
      return acc;
    }, {} as Record<PracticeType, string[]>);
  }, [wrongList, filterVersion, practiceFilters]);

  const updatePracticeFilter = (part: PracticeType, key: keyof PracticeFilterState, value: string) => {
    setPracticeFilters((prev) => ({
      ...prev,
      [part]: {
        ...prev[part],
        [key]: value,
        ...(key === 'level' ? { subPart: 'all' } : {}),
      },
    }));
  };

  useEffect(() => {
    setPracticeFilters((prev) => {
      let changed = false;
      const next = { ...prev };

      PRACTICE_TYPES.forEach(({ part }) => {
        const selectedVersion = prev[part]?.version;
        const normalizedVersion = filterVersion;

        const levelOptions = Array.from(
          new Set(
            wrongList
              .filter((item) => item.practiceType === part)
              .filter((item) => !normalizedVersion || item.bankVersion === normalizedVersion)
              .map((item) => String(item.level))
              .filter(Boolean)
          )
        ).sort((a, b) => Number(a) - Number(b));

        const selectedLevel = prev[part]?.level;
        const normalizedLevel = levelOptions.includes(selectedLevel) ? selectedLevel : (levelOptions[0] || '');

        if (normalizedVersion !== selectedVersion || normalizedLevel !== selectedLevel) {
          changed = true;
          next[part] = {
            ...prev[part],
            version: normalizedVersion,
            level: normalizedLevel,
            subPart: 'all',
          };
        }
      });

      return changed ? next : prev;
    });
  }, [filterVersion, wrongList]);

  useEffect(() => {
    if (!availableStatsVersions.length) {
      if (selectedStatsVersion) setSelectedStatsVersion('');
      if (selectedStatsLevel) setSelectedStatsLevel('');
      return;
    }

    if (!selectedStatsVersion || !availableStatsVersions.includes(selectedStatsVersion)) {
      setSelectedStatsVersion(availableStatsVersions[0]);
      return;
    }

    if (!availableStatsLevels.length) {
      if (selectedStatsLevel) setSelectedStatsLevel('');
      return;
    }

    if (!selectedStatsLevel || !availableStatsLevels.includes(selectedStatsLevel)) {
      setSelectedStatsLevel(availableStatsLevels[0]);
    }
  }, [availableStatsLevels, availableStatsVersions, selectedStatsLevel, selectedStatsVersion]);

  useEffect(() => {
    const loadSelectedStats = () => {
      if (!selectedStatsVersion || !selectedStatsLevel) {
        setRecentLevelStats(null);
        return;
      }

      const selectedTests = serverHistory.filter(item => !item.status || item.status === 'completed').filter(
        (item) => String(item.bankVersion) === selectedStatsVersion && normalizeHskLevelValue(item.level) === selectedStatsLevel
      );

      if (selectedTests.length === 0) {
        setRecentLevelStats(null);
        return;
      }

      const computed = calculateRecentLevelStats(Number(selectedStatsLevel), selectedTests);
      setRecentLevelStats({
        ...computed,
        bankVersion: selectedStatsVersion,
      });
    };

    loadSelectedStats();
  }, [selectedStatsLevel, selectedStatsVersion, serverHistory]);

  const updateReviewCount = (part: PracticeType, rawValue: string) => {
    const parsed = Number(rawValue);
    setReviewCounts((prev) => ({
      ...prev,
      [part]: Number.isFinite(parsed) ? Math.max(1, Math.floor(parsed)) : DEFAULT_REVIEW_COUNTS[part],
    }));
  };

  // Cluster wrong items by question_group so questions from the same
  // question_group always appear together. Items without a group are
  // treated as singleton clusters.
  const clusterByQuestionGroup = (items: WrongPracticeItem[]): WrongPracticeItem[][] => {
    const clusters = new Map<string, WrongPracticeItem[]>();
    items.forEach((item, idx) => {
      const key = item.questionGroup ? `group:${item.questionGroup}` : `single:${item.id}:${idx}`;
      if (!clusters.has(key)) clusters.set(key, []);
      clusters.get(key)!.push(item);
    });
    return Array.from(clusters.values()).map((cluster) =>
      cluster.slice().sort((a, b) => {
        const sa = a.groupSeq ?? 0;
        const sb = b.groupSeq ?? 0;
        return sa - sb;
      })
    );
  };

  const startWrongPractice = (part: PracticeType) => {
    const pool = filteredWrongByPractice[part] || [];
    if (pool.length === 0) return;
    const desiredCount = Math.max(1, Math.floor(reviewCounts[part] || DEFAULT_REVIEW_COUNTS[part]));

    // Cluster first, sample whole clusters until we reach the desired count.
    const shuffledClusters = clusterByQuestionGroup(pool).sort(() => Math.random() - 0.5);
    const pickedItems: WrongPracticeItem[] = [];
    for (const cluster of shuffledClusters) {
      if (pickedItems.length >= desiredCount) break;
      pickedItems.push(...cluster);
    }

    const sample = pickedItems.map((item) => ({
      id: item.id,
      level: item.level || 3,
      part: item.subPart,
      question: item.question,
      options: item.options,
      correctAnswer: item.correctAnswer,
                correctAnswers: item.correctAnswers,
      media: item.media || undefined,
      explanation: item.explanation || undefined,
      explanationId: item.explanationId || undefined,
      bankVersion: item.bankVersion,
      wrongReviewSource: item.wrongReviewSource,
      questionGroup: item.questionGroup ?? null,
      groupSeq: item.groupSeq ?? null,
    }));

    const practiceLevel = sample[0]?.level || 3;
    startTest(practiceLevel, part, sample, `practice-${part}-${Date.now()}`);
    // Mark the destination with ?review=1 so the test page opts into review mode
    // (uses the in-memory practice session, skips all server start/resume/save).
    window.location.href = `${getPracticePath(part, practiceLevel)}?review=1`;
  };

  const handleViewWrong = (part: PracticeType, selectedItems?: WrongPracticeItem[]) => {
    const items = selectedItems || filteredWrongByPractice[part] || [];
    if (items.length === 0) return;

    // Reorder so items sharing a question_group appear consecutively.
    const orderedItems: WrongPracticeItem[] = clusterByQuestionGroup(items).flat();

    const session: TestSession = {
      id: `wrong-view-${part}-${Date.now()}`,
      userId: 'wrong-history',
      level: orderedItems[0]?.level || 3,
      part,
      questions: orderedItems.map((item) => ({
        id: item.id,
        level: item.level || 3,
        part: item.subPart,
        question: item.question,
        options: item.options,
        correctAnswer: item.correctAnswer,
                correctAnswers: item.correctAnswers,
        explanation: item.explanation || undefined,
        explanationId: item.explanationId || undefined,
        media: item.media || undefined,
        questionGroup: item.questionGroup ?? null,
        groupSeq: item.groupSeq ?? null,
      })),
      answers: orderedItems.map((item) => {
        if (item.choseBuzhidao) return -1;
        if (Array.isArray(item.answerContent)) {
          return { sequence: item.answerContent, isCorrect: false };
        }
		if (item.answerContent && typeof item.answerContent === 'object') {
			if (Array.isArray(item.answerContent.sequence)) return { sequence: item.answerContent.sequence, isCorrect: false };
			if (typeof item.answerContent.text === 'string') return { text: item.answerContent.text, isCorrect: false };
		}
        if (typeof item.answerContent === 'string' && item.answerContent.trim()) {
          return { text: item.answerContent, isCorrect: false };
        }
        return item.answer ?? null;
      }),
      score: 0,
      completedAt: orderedItems[0]?.completedAt || new Date().toISOString(),
      createdAt: orderedItems[0]?.completedAt || new Date().toISOString(),
    };

    setDetailSession(session);
    setDetailBankVersion(String(orderedItems[0]?.bankVersion || '2.0'));
    setViewMode('detail');
    setReviewIndex(0);
    window.scrollTo(0, 0);
  };

  const handleViewDetail = async (testId: string) => {
    setLoadingDetail(true);
    try {
        const localSession = (testHistory || []).find((session) => session.id === testId);
		// Only client-only Review Wrong sessions may use persisted Zustand data.
		// Every real test must load the authoritative answers/results from D1;
		// cloud test questions deliberately carry correctAnswer=-1 while in progress.
		if (localSession && shouldUseLocalTestReview(testId)) {
            setDetailSession(localSession);
            setDetailBankVersion(String((localSession.questions?.[0] as any)?.bankVersion || '2.0'));
            setViewMode('detail');
            setReviewIndex(0);
            window.scrollTo(0, 0);
            return;
        }
        console.log('Fetching details for test:', testId);
        const data = await api.getTestDetail(testId, token!);
        console.log('Test details received:', data);
        
        // Backend returns the test object directly mixed with items
        // { id: "...", items: [...] }
        if (!data || !data.id) {
            throw new Error('Invalid test data received');
        }

        // Map backend response to TestSession
        const session: TestSession = {
            id: data.id,
            userId: data.user_id,
            level: data.level,
            part: data.part,
            systemType: data.system_type,
            questions: (data.items || []).map((item: any) => ({
                id: item.question_id || item.id,
                level: data.level,
                part: item.part || data.part, // Ensure part is taken from item if available
                question: item.question_text || item.question || 'Question text unavailable',
                options: item.options ? (typeof item.options === 'string' ? JSON.parse(item.options) : item.options) : [],
                gradingPending: item.grading_pending,
                    correctAnswer: item.correct_option_index ?? item.correctAnswer ?? -1,
                correctAnswers: item.correct_option_indices,
                explanation: item.explanation,
                explanationId: item.explanation_id || undefined,
				media: item.media || item.audio,
				reviewCorrect: typeof item.correct === 'boolean' ? item.correct : undefined,
            })),
            answers: (data.items || []).map((item: any) => {
                // Check for writing-specific answer formats first
                // Also check item.answer_content (which backend sends instead of chosen_sequence/chosen_text_answer)
                
                const storedWritingAnswer = getStoredWritingAnswer(item);
                if (storedWritingAnswer) return storedWritingAnswer;
                
                // Fallback to standard index-based answer
                return item.chosen_option_index ?? (item.chose_buzhidao ? -1 : null);
            }),
            score: data.score,
            completedAt: data.completed_at || data.date_taken,
            createdAt: data.date_taken,
            weightedScore: Number(data.weighted_score || 0),
            maxScore: String(data.bank_version || '') === 'CSCA' ? 100 : Number(data.max_score || 0),
            sectionScores: data.section_scores,
        };

        // Assertion: Check for data consistency
        // If score > 0, we expect at least one valid answer
        const hasAnswers = session.answers.some(a => {
            if (a === null || a === -1) return false; // Unanswered / Unknown
            if (typeof a === 'object') {
                 // Check if actually answered (sequence length > 0 or text length > 0)
                 if ('sequence' in a && Array.isArray(a.sequence) && a.sequence.length === 0) return false;
                 if ('text' in a && (!a.text || (a.text as string).trim() === '')) return false;
                 return true;
            }
            return true; // Standard index answer
        });
        
        if (session.score > 0 && !hasAnswers) {
             const msg = `Data Consistency Error: Test ${session.id} has positive score (${session.score}) but no valid answers found in review data.`;
             console.error(msg, session);
             throw new Error(msg);
        }

        setDetailSession(session);
        setDetailBankVersion(String(data.bank_version || serverHistory.find(test => test.id === testId)?.bankVersion || '2.0'));
        setViewMode('detail');
        setReviewIndex(0);
        window.scrollTo(0, 0);
    } catch (e) {
        console.error(e);
        setError('Failed to load test details');
    } finally {
        setLoadingDetail(false);
    }
  };

  const scrollToCard = (i: number) => {
    const el = document.getElementById(`q-${i}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setReviewIndex(i);
  };

  const playAudio = (src: string) => {
      if (audioElement) {
          audioElement.pause();
      }
      const audio = new Audio(src);
      audio.onended = () => setIsPlaying(false);
      setAudioElement(audio);
      audio.play().then(() => setIsPlaying(true)).catch(e => console.error(e));
  };

  if (viewMode === 'detail' && detailSession) {
      const total = detailSession.questions.length;
      const hasIndonesianExplanation = detailBankVersion === 'CSCA';
      const reviewExplanation = (question: typeof detailSession.questions[number]) =>
        explanationLanguage === 'ID' && question.explanationId ? question.explanationId : question.explanation;
      const shouldShowPinyin = detailBankVersion === '2.0';
      const renderReviewText = (text?: string | null, className = '') => {
        const safeText = String(text || '');
		if (detailBankVersion === 'CSCA') return <MathText content={safeText} className={className} />;
        const imagePath = extractImagePath(safeText);
        const displayText = imagePath ? stripImagePathFromText(safeText) : safeText;
        const parts = displayText.split(/(\*{1,2}[^*]+\*{1,2})/g);
        const nodes = parts.map((part, i) => {
          const m = part.match(/^\*{1,2}([^*]+)\*{1,2}$/);
          if (m) {
            return <strong key={i}>{shouldShowPinyin ? <PinyinText text={m[1]} className={className} /> : m[1]}</strong>;
          }
          return shouldShowPinyin ? <PinyinText key={i} text={part} className={className} /> : part;
        });
        return <span className={className}>{nodes}{imagePath && <img src={resolveMediaUrl(imagePath)} alt="题目配图" className="mt-3 max-h-80 max-w-full rounded-lg border border-gray-200 bg-white object-contain" />}</span>;
      };
      const sessionPart = String(detailSession.part || '').toLowerCase();
      const getQuestionWeight = (q: any, idx: number) => {
		if (detailBankVersion === 'CSCA') { const graded = detailSession.questions.filter(q => !isCscaPendingReview(q)).length; return !isCscaPendingReview(q) && graded > 0 ? 100 / graded : 0; }
        const directWeight = getHistoryQuestionWeight(q.part || detailSession.part, detailSession.level);
        if (directWeight !== 1 || String(q.part || '').trim()) return directWeight;
        const oneBased = idx + 1;
        if (sessionPart === 'simulation') {
          if (oneBased >= 1 && oneBased <= 20) return 2;
          if (oneBased >= 21 && oneBased <= 50) return 3;
          if (oneBased >= 51 && oneBased <= 55) return 2;
          if (oneBased >= 56 && oneBased <= 70) return 4;
          if (oneBased >= 71 && oneBased <= 75) return 12;
          if (oneBased >= 76 && oneBased <= 80) return 8;
        }
        return directWeight;
      };
      const UNKNOWN_ANSWER = -1;
      const isCorrect = (i: number) => {
        const q = detailSession.questions[i];
        const ans = detailSession.answers[i];
		if (typeof q?.reviewCorrect === 'boolean') return q.reviewCorrect;
        const aiScoredPrompt = isAiScoredWritingPrompt(q);
        if (ans && typeof ans === 'object' && 'isCorrect' in ans) {
          return (ans as any).isCorrect === true;
        }
        if (aiScoredPrompt) return false;
        const isWriting = String(q.part || '').toLowerCase().startsWith('w') || sessionPart === 'writing';
        if (isWriting && typeof ans === 'object' && ans !== null) {
          const a = ans as any;
          if (a.sequence) {
            const userSeq = (a.sequence || []).map((t: string) => String(t).trim()).filter(Boolean);
            const correctSeq = buildCorrectSequence(q);
            return userSeq.length === correctSeq.length && correctSeq.every((t: string, k: number) => t === userSeq[k]);
          }
          if (a.text) {
            const userText = String(a.text || '').trim();
            const correctText = String(q.options?.[0] || '').trim();
            return userText === correctText;
          }
        }
        return ans !== null && ans !== UNKNOWN_ANSWER && (detailSession.questions[i].correctAnswers || [detailSession.questions[i].correctAnswer]).includes(ans as number);
      };
      const getAwardedScore = (i: number) => {
        const ans = detailSession.answers[i];
        const weight = getQuestionWeight(detailSession.questions[i], i);
        if (ans && typeof ans === 'object' && typeof (ans as any).awardedScore === 'number') {
          return Number((ans as any).awardedScore || 0);
        }
        return isCorrect(i) ? weight : 0;
      };
      const weightedScore = typeof detailSession.weightedScore === 'number'
        ? Number(detailSession.weightedScore || 0)
        : detailSession.questions.reduce((sum, q, i) => sum + getAwardedScore(i), 0);
      const maxWeightedScore = typeof detailSession.maxScore === 'number' && detailSession.maxScore > 0
        ? Number(detailSession.maxScore || 0)
        : detailSession.questions.reduce((sum, q, i) => sum + getQuestionWeight(q, i), 0);
      const score = detailSession.questions.reduce((sum, _q, i) => sum + (isCorrect(i) ? 1 : 0), 0);
      const gradableTotal = detailSession.questions.filter(q => !isCscaPendingReview(q)).length;
      const percentage = maxWeightedScore > 0 ? Math.round((weightedScore / maxWeightedScore) * 100) : 0;
      const detailSessionId = String(detailSession.id || '');
      const isReviewDetail = detailSessionId.startsWith('practice-') || detailSessionId.startsWith('wrong-view-');
      const isWrongViewDetail = detailSessionId.startsWith('wrong-view-');
      const detailTitle = isWrongViewDetail ? 'View Wrong' : detailBankVersion === 'CSCA' ? `CSCA ${getCscaSubjectName(detailSession.level)}试卷解析` : 'Test Review';
      const detailSubtitle = isWrongViewDetail
        ? 'Viewing the wrong questions from your current Review & Practice filters.'
        : detailBankVersion === 'CSCA' ? '试卷记录与逐题解析。' : 'Review record, score not counted.';

	  const isUnanswered = (i: number) => {
        if (isCscaPendingReview(detailSession.questions[i])) return false;
		const answer = detailSession.answers[i] as any;
		if (answer == null) return true;
		if (typeof answer !== 'object') return false;
		const hasSequence = Array.isArray(answer.sequence) && answer.sequence.length > 0;
		const hasText = typeof answer.text === 'string' && answer.text.trim().length > 0;
		return !hasSequence && !hasText;
	  };
	  const isUnknown = (i: number) => detailSession.answers[i] === UNKNOWN_ANSWER || isUnanswered(i);
      const isAnsweredWrong = (i: number) => !isCscaPendingReview(detailSession.questions[i]) && !isCorrect(i) && !isUnknown(i);
      
	  const isOrderQuestion = (q: any) => isSequenceReviewQuestion(q, detailSession.level);
	  const isWritingPart = (q: any) => isOrderQuestion(q) || String(q.part || '').toLowerCase().startsWith('w') || sessionPart === 'writing';

      // Check if ANY question is writing type to determine view mode
      // If mixed test, we use the standard view but writing questions render differently
      // BUT if the WHOLE test is writing, we might use the special writing view (if intended).
      // The original code used 'isWritingPart' boolean to switch entire view.
      // Let's keep it simple: if sessionPart is 'writing', use special view.
      // Otherwise use list view which handles mixed types.
      const isPureWritingTest = sessionPart === 'writing';

      if (isPureWritingTest) {
        return (
          <div className="max-w-6xl mx-auto pb-12">
            <div className="bg-white shadow-xl shadow-indigo-500/5 rounded-3xl p-8 border border-indigo-50">
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
                    <div>
                         <button 
                            onClick={() => { setViewMode('list'); setDetailBankVersion(null); }}
                            className="flex items-center text-gray-500 hover:text-[#4F46E5] mb-2 transition-colors font-bold"
                         >
                             <ChevronLeft className="w-5 h-5 mr-1" />
                             Back to History
                         </button>
                        <h1 className="text-3xl font-bold text-[#312E81]">{detailTitle}</h1>
                        {isReviewDetail ? (
                            <p className="text-[#4F46E5] font-semibold mt-1">{detailSubtitle}</p>
                        ) : (
                            <div className="flex gap-4 mt-1">
                                <p className="text-[#4F46E5] font-semibold">
                                    Correct: {score}/{gradableTotal}
                                </p>
                                {maxWeightedScore > 0 ? (
                                  <p className="text-[#4F46E5] font-semibold border-l pl-4 border-indigo-100">
                                      Score: {detailBankVersion === 'CSCA' ? weightedScore.toFixed(1) : weightedScore}/{maxWeightedScore}
                                  </p>
                                ) : (
                                  <p className="text-[#4F46E5] font-semibold border-l pl-4 border-indigo-100">
                                      AI Review Available
                                  </p>
                                )}
                            </div>
                        )}
                    </div>
                    {!isReviewDetail && detailBankVersion !== 'CSCA' && (
                    <div>
                         <button
                            onClick={() => window.location.href = `/test/review/${detailSession.id}`}
                            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold shadow-lg shadow-pink-500/30 hover:shadow-pink-500/40 active:scale-95 transition-all flex items-center gap-2"
                         >
                            <Trophy className="w-5 h-5" />
                            Review Weak Vocab
                         </button>
                    </div>
                    )}
                </div>

                <div className="space-y-6">
                    {detailSession.questions.map((qItem, idx) => {
                      const wa = detailSession.answers[idx] as any; // Writing answers are complex objects usually, but let's check format
                      // Actually, for history, 'answers' array usually contains just the chosen index or similar. 
                      // BUT for writing, we need the actual text/sequence.
                      // The backend 'getTestDetail' returns 'items' with 'answer' (the text/sequence) or 'chosen_option_index'.
                      // We mapped it to 'answers' in loadHistory.
                      // Wait, our 'answers' map in handleViewDetail only maps chosen_option_index.
                      // We need to fix handleViewDetail to include text/sequence for writing!
                      
                      // Let's assume we fixed the mapping below first.
                      // For now, let's access the raw item if possible? No, we need to map it correctly.
                      
                      const orderType = isOrderQuestion(qItem);
                      
                      // In handleViewDetail we need to map:
                      // answers: (data.items || []).map((item: any) => {
                      //    if (item.chosen_sequence) return { sequence: item.chosen_sequence };
                      //    if (item.chosen_text_answer) return { text: item.chosen_text_answer };
                      //    return item.chosen_option_index ?? (item.chose_buzhidao ? -1 : null)
                      // }),
                      
                      // Since we can't easily change the mapping in the huge file without potential breakage, 
                      // let's assume we will update the mapping in the next SearchReplace.
                      
                      const answerData = detailSession.answers[idx];
                      const userSeq = ((answerData as any)?.sequence || []).map((t: string) => String(t).trim()).filter(Boolean);
                      const userText = String((answerData as any)?.text || '').trim();
                      const aiScoredPrompt = isAiScoredWritingPrompt(qItem);
                      const gradingKey = getReviewGradingKey(String(detailSession.id || ''), idx);
                      const grading = (answerData as any)?.grading || writingGradings[gradingKey];
                      const awardedScore = typeof (answerData as any)?.awardedScore === 'number' ? Number((answerData as any).awardedScore || 0) : null;
                      const aiScore = typeof (answerData as any)?.aiScore === 'number' ? Number((answerData as any).aiScore || 0) : null;
                      const maxScore = typeof (answerData as any)?.maxScore === 'number'
                        ? Number((answerData as any).maxScore || 0)
                        : getQuestionWeight(qItem, idx);
                      const scorePercentage = aiScore != null
                        ? aiScore
                        : (awardedScore != null && maxScore > 0 ? (awardedScore / maxScore) * 100 : 0);
                      const correctSequence = orderType ? buildCorrectSequence(qItem) : [];
					  const questionImagePath = resolveQuestionImagePath(qItem, detailSession.level);
                      
                      let cardCorrect = false;
                      let cardUnknown = false;
                      
                      if (orderType) {
                        cardCorrect = userSeq.length === correctSequence.length && correctSequence.every((t: string, i: number) => t === userSeq[i]);
                        cardUnknown = userSeq.length === 0;
                      } else if (aiScoredPrompt) {
                        cardUnknown = !userText;
                      } else {
                        // W2
                         const correctText = String(qItem.options?.[0] || '').trim();
                         cardCorrect = userText.length > 0 && userText === correctText;
                         cardUnknown = !userText;
                      }

                      const cardClass = isCscaPendingReview(qItem) ? 'border-slate-200' : aiScoredPrompt
                        ? (cardUnknown
                          ? 'border-orange-300 ring-2 ring-orange-200'
                          : getHistoryScoreCardClass(scorePercentage))
                        : cardCorrect
                          ? 'border-green-500 ring-2 ring-green-400'
                          : 'border-red-500 ring-2 ring-red-400';
                      
                      return (
                        <div key={idx} className={`p-6 border rounded-lg bg-white ${cardClass}`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="text-sm text-gray-600">
                                第 {idx + 1} 题
                                <span className="text-xs text-gray-400 ml-2">({qItem.id})</span>
                            </div>
                            <div className="text-sm">
                              {isCscaPendingReview(qItem) ? <span className="text-slate-600 font-semibold">待核对 · 不计分</span> : aiScoredPrompt ? (
                                cardUnknown ? (
                                  <span className="text-orange-600 font-semibold flex items-center gap-1"><HelpCircle className="w-4 h-4"/> 未作答</span>
                                ) : (
                                  <span className={`${getHistoryScoreTextClass(scorePercentage)} font-semibold flex items-center gap-1`}><CheckCircle className="w-4 h-4"/> AI 已评分{awardedScore != null ? ` ${awardedScore}/${maxScore}` : ''}</span>
                                )
                              ) : cardCorrect ? (
                                <span className="text-green-600 font-semibold flex items-center gap-1"><CheckCircle className="w-4 h-4"/> 回答正确</span>
                              ) : cardUnknown ? (
                                <span className="text-orange-600 font-semibold flex items-center gap-1"><HelpCircle className="w-4 h-4"/> 未作答</span>
                              ) : (
                                <span className="text-red-600 font-semibold flex items-center gap-1"><AlertCircle className="w-4 h-4"/> 回答错误</span>
                              )}
                            </div>
                          </div>
                          <div className="mb-4 text-lg font-semibold text-gray-800 whitespace-pre-line">{renderReviewText(qItem.question)}</div>
						  {questionImagePath && !extractImagePath(qItem.question) && (
                            <div className="mb-4">
                              <img
								src={resolveMediaUrl(questionImagePath)}
                                alt={`Writing prompt ${idx + 1}`}
                                className="max-h-80 w-auto rounded-lg border border-gray-200 bg-gray-50 object-contain"
                              />
                            </div>
                          )}
                          
                          {orderType ? (
                            <div className="space-y-3">
                              <div className="p-4 border rounded bg-gray-50">
                                <div className="text-sm text-gray-600 mb-2">你的答案</div>
                                <div className="flex flex-wrap gap-2">
                                  {userSeq.map((t: string, i: number) => (
                                    <span key={i} className="px-2 py-1 border rounded bg-white shadow-sm">{renderReviewText(t)}</span>
                                  ))}
                                  {userSeq.length === 0 && <span className="text-gray-400 italic">未作答</span>}
                                </div>
                              </div>
                              <div className="p-4 border rounded bg-green-50">
                                <div className="text-sm text-gray-600 mb-2">正确答案</div>
                                <div className="flex flex-wrap gap-2">
                                   {/* We might not have correct sequence easily available, showing options as fallback or specific correct answer field if mapped */}
                                   {correctSequence.map((t: string, i: number) => (
                                     <span key={i} className="px-2 py-1 border rounded bg-white shadow-sm">{renderReviewText(t)}</span>
                                   ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="p-4 border rounded bg-gray-50">
                                <div className="text-sm text-gray-600 mb-2">你的答案</div>
                                <div className="text-gray-800">{userText ? renderReviewText(userText) : '未作答'}</div>
                              </div>
                              {!aiScoredPrompt && (
                                <div className="p-4 border rounded bg-green-50">
                                  <div className="text-sm text-gray-600 mb-2">参考答案</div>
                                  <div className="text-green-700">{renderReviewText(qItem.options[0] || '')}</div>
                                </div>
                              )}
                              {aiScoredPrompt && userText && (
                                <div className={`p-4 border rounded space-y-3 ${getHistoryScorePanelClass(scorePercentage)}`}>
                                  {awardedScore != null && (
                                    <div className="flex items-center justify-between rounded-xl bg-white px-4 py-3 border border-indigo-100">
                                      <div>
                                        <div className={`text-sm font-bold ${getHistoryScoreTextClass(scorePercentage)}`}>AI 计分</div>
                                        <div className="text-xs text-gray-500">按百分比分数折算为本题得分</div>
                                      </div>
                                      <div className="text-right">
                                        <div className={`text-2xl font-bold ${getHistoryScoreTextClass(scorePercentage)}`}>{awardedScore}<span className="text-sm text-gray-400">/{maxScore}</span></div>
                                        {aiScore != null && (
                                          <div className={`text-xs ${getHistoryScoreTextClass(scorePercentage)}`}>AI: {aiScore}/100</div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {!grading ? (
                                    <div>
                                      <button
                                        onClick={() => handleGradeReviewWriting(String(detailSession.id || ''), idx, qItem.question, userText)}
                                        disabled={writingGradingLoading[gradingKey]}
                                        className="px-4 py-2 rounded-xl bg-[#4F46E5] text-white font-bold text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {writingGradingLoading[gradingKey] ? 'AI 批改中…' : '获取 AI 分析与参考范文'}
                                      </button>
                                      {writingGradingError[gradingKey] && (
                                        <div className="mt-2 text-sm text-red-600">{writingGradingError[gradingKey]}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="space-y-3">
                                      <div className="flex items-center justify-between">
                                        <div className={`text-sm font-bold ${getHistoryScoreTextClass(Number(grading.score || scorePercentage))}`}>AI 分析</div>
                                        {typeof grading.score === 'number' && (
                                          <div className={`text-2xl font-bold ${getHistoryScoreTextClass(grading.score)}`}>{grading.score}<span className="text-sm text-gray-400">/100</span></div>
                                        )}
                                      </div>
                                      {grading.summary && <div className="text-sm text-gray-700">{grading.summary}</div>}
                                      {Array.isArray(grading.dimensions) && grading.dimensions.length > 0 && (
                                        <div className="grid grid-cols-2 gap-2">
                                          {grading.dimensions.map((d, i) => (
                                            <div key={i} className="rounded border border-indigo-100 bg-white p-2 text-sm">
                                              <div className="flex justify-between">
                                                <span className="font-semibold text-gray-700">{d.name}</span>
                                                {typeof d.score === 'number' && <span className={`font-bold ${getHistoryScoreTextClass(d.score)}`}>{d.score}</span>}
                                              </div>
                                              {d.comment && <div className="mt-1 text-xs text-gray-500">{d.comment}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {Array.isArray(grading.corrections) && grading.corrections.length > 0 && (
                                        <div className="space-y-2">
                                          <div className="text-sm font-semibold text-gray-700">修改建议</div>
                                          {grading.corrections.map((c, i) => (
                                            <div key={i} className="border-l-2 border-amber-300 pl-2 text-sm">
                                              <span className="text-red-500 line-through">{c.original}</span> → <span className="text-green-700">{c.suggestion}</span>
                                              {c.reason && <div className="text-xs text-gray-500">{c.reason}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {grading.sample && (
                                        <div className="rounded bg-green-50 p-3">
                                          <div className="mb-1 text-sm font-semibold text-green-800">AI 参考范文</div>
                                          <div className="text-sm text-gray-700 whitespace-pre-line">{grading.sample}</div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                           {(qItem.explanation || isWrongViewDetail) && (
                                <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-blue-800 text-sm">
                                    <span className="font-bold block mb-1">解析:</span>
                                    {reviewExplanation(qItem)
                                        ? renderReviewText(reviewExplanation(qItem))
                                        : <span className="italic text-blue-600/70">（暂无解析）</span>}
                                </div>
                            )}
                        </div>
                      );
                    })}
                </div>
            </div>
          </div>
        );
      }

      return (
          <div className="max-w-6xl mx-auto pb-12">
            <div className="bg-white shadow-xl shadow-indigo-500/5 rounded-3xl p-8 border border-indigo-50">
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
                    <div>
                         <button 
                            onClick={() => { setViewMode('list'); setDetailBankVersion(null); }}
                            className="flex items-center text-gray-500 hover:text-[#4F46E5] mb-2 transition-colors font-bold"
                         >
                             <ChevronLeft className="w-5 h-5 mr-1" />
                             Back to History
                         </button>
                        <h1 className="text-3xl font-bold text-[#312E81]">{detailTitle}</h1>
                        {isReviewDetail ? (
                            <p className="text-[#4F46E5] font-semibold mt-1">{detailSubtitle}</p>
                        ) : (
                            <div className="flex gap-4 mt-1">
                                <p className="text-[#4F46E5] font-semibold">
                                    Correct: {score}/{total}
                                </p>
                                <p className="text-[#4F46E5] font-semibold border-l pl-4 border-indigo-100">
                                    Score: {detailBankVersion === 'CSCA' ? weightedScore.toFixed(1) : weightedScore}/{maxWeightedScore}
                                </p>
                            </div>
                        )}
                    </div>
                    {hasIndonesianExplanation && (
                      <div className="flex items-center gap-3">
                        <CscaExplanationLanguageSwitch language={explanationLanguage} onChange={changeExplanationLanguage} />
                      </div>
                    )}
                    {!isReviewDetail && detailBankVersion !== 'CSCA' && (
                    <div>
                         <button
                            onClick={() => window.location.href = `/test/review/${detailSession.id}`}
                            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold shadow-lg shadow-pink-500/30 hover:shadow-pink-500/40 active:scale-95 transition-all flex items-center gap-2"
                         >
                            <Trophy className="w-5 h-5" />
                            Review Weak Vocab
                         </button>
                    </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    {/* Sidebar Question Map */}
                    <div className="md:col-span-3">
                        <div className="sticky top-24 bg-gray-50 rounded-2xl p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <h3 className="font-bold text-gray-700 mb-4">Question Map</h3>
                            <div className="grid grid-cols-5 gap-2">
                                {detailSession.questions.map((_, i) => {
                                    const btnCorrect = isCorrect(i);
                                    const btnUnknown = isUnknown(i);
                                    const btnWrong = isAnsweredWrong(i);
                                    const base = 'flex items-center justify-center h-10 rounded-lg text-sm font-bold transition-all';
                                    const color = btnCorrect
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : (btnUnknown || btnWrong)
                                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                            : 'bg-gray-200 text-gray-600';
                                    const active = i === reviewIndex ? 'ring-2 ring-offset-2 ring-[#4F46E5]' : '';
                                    return (
                                        <button key={i} onClick={() => scrollToCard(i)} className={`${base} ${color} ${active}`}>{i + 1}</button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Questions List */}
                    <div className="md:col-span-9 space-y-8">
                        {detailSession.questions.map((qItem, idx) => {
                            const ans = detailSession.answers[idx];
                            const writingAnswer = (typeof ans === 'object') ? ans : null;
                            const cardCorrect = isCorrect(idx);
                            const cardUnknown = isUnknown(idx);
                            const aiScoredPrompt = isAiScoredWritingPrompt(qItem);
                            const awardedScore = typeof (writingAnswer as any)?.awardedScore === 'number' ? Number((writingAnswer as any).awardedScore || 0) : null;
                            const maxScore = typeof (writingAnswer as any)?.maxScore === 'number'
                              ? Number((writingAnswer as any).maxScore || 0)
                              : getQuestionWeight(qItem, idx);
                            const aiScore = typeof (writingAnswer as any)?.aiScore === 'number' ? Number((writingAnswer as any).aiScore || 0) : null;
                            const scorePercentage = aiScore != null
                              ? aiScore
                              : (awardedScore != null && maxScore > 0 ? (awardedScore / maxScore) * 100 : 0);
                            const cardClass = isCscaPendingReview(qItem) ? 'border-slate-200' : aiScoredPrompt
                              ? (cardUnknown ? 'border-orange-300 ring-2 ring-orange-200' : getHistoryScoreCardClass(scorePercentage))
                              : cardCorrect
                                ? 'border-green-200 shadow-green-500/5'
                                : 'border-red-200 shadow-red-500/5';
                            
                            const isWriting = isWritingPart(qItem);
                            const orderType = isOrderQuestion(qItem);
                            const correctSequence = orderType ? buildCorrectSequence(qItem) : [];
                            const userSeq = (writingAnswer as any)?.sequence || [];
                            const userText = (writingAnswer as any)?.text || '';
                            const grading = (writingAnswer as any)?.grading || null;
                            const isListening = String(qItem.part || '').toLowerCase().includes('listening') || String(qItem.part || '').toLowerCase().startsWith('l') || (detailSession.part === 'listening');
							const questionImagePath = resolveQuestionImagePath(qItem, detailSession.level);

                            return (
                                <div key={idx} id={`q-${idx}`} className={`p-6 md:p-8 bg-white border-2 rounded-3xl shadow-sm ${cardClass}`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="px-3 py-1 bg-gray-100 rounded-full text-sm font-bold text-gray-600">
                                            Question {idx + 1}
                                            <span className="text-xs text-gray-400 font-normal ml-2">({qItem.id})</span>
                                        </div>
                                        <div className="text-sm font-bold">
                                            {isCscaPendingReview(qItem) ? <span className="text-slate-600">待核对 · 不计分</span> : aiScoredPrompt && !cardUnknown ? (
                                                <span className={`${getHistoryScoreTextClass(scorePercentage)} flex items-center gap-1`}><CheckCircle className="w-4 h-4" /> AI {awardedScore ?? 0}/{maxScore}</span>
                                            ) : cardCorrect ? (
                                                <span className="text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Correct</span>
                                            ) : cardUnknown ? (
												<span className="text-orange-500 flex items-center gap-1"><HelpCircle className="w-4 h-4" /> {isUnanswered(idx) ? 'Unanswered' : "Don't Know"}</span>
                                            ) : (
                                                <span className="text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Incorrect</span>
                                            )}
                                        </div>
                                    </div>
                                    {qItem.question && qItem.question !== 'Question text unavailable' && (
                                         <div className="mb-6 text-xl font-medium text-[#312E81] whitespace-pre-line">{renderReviewText(qItem.question)}</div>
                                     )}
									 {isWriting && questionImagePath && !extractImagePath(qItem.question) && (
                                         <div className="mb-6">
                                             <img
												 src={resolveMediaUrl(questionImagePath)}
                                                 alt={`Writing prompt ${idx + 1}`}
                                                 className="max-h-96 w-auto rounded-lg border border-gray-200 bg-gray-50 object-contain"
                                             />
                                         </div>
                                     )}
									 {isListening && (!qItem.question || qItem.question === 'Question text unavailable') && (qItem.media || qItem.id) && (
                                         <div className="mb-4 text-gray-500 font-medium italic flex items-center gap-2">
                                             <Volume2 className="w-5 h-5"/> Please listen to the audio below
                                         </div>
                                     )}
									 {isListening && (qItem.media || qItem.id) && (
                                         <div className="mb-6">
                                             <audio controls src={qItem.media ? (qItem.media.startsWith('http') ? qItem.media : `${API_BASE_URL}/media${qItem.media.startsWith('/') ? '' : '/'}${qItem.media}`) : `${API_BASE_URL}/media/audio/${qItem.id}.mp3`} className="w-full rounded-lg" />
                                         </div>
                                     )}
                                    
                                    {isWriting ? (
                                         orderType ? (
                                            <div className="space-y-3">
                                              <div className="p-4 border rounded bg-gray-50">
                                                <div className="text-sm text-gray-600 mb-2">User Answer</div>
                                                <div className="flex flex-wrap gap-2">
                                                  {userSeq.map((t: string, i: number) => (
                                                    <span key={i} className="px-2 py-1 border rounded bg-white shadow-sm">{renderReviewText(t)}</span>
                                                  ))}
                                                  {userSeq.length === 0 && <span className="text-gray-400 italic">No answer</span>}
                                                </div>
                                              </div>
                                              <div className="p-4 border rounded bg-green-50">
                                                <div className="text-sm text-gray-600 mb-2">Options (Correct Order usually implied)</div>
                                                <div className="flex flex-wrap gap-2">
                                                   {correctSequence.map((t: string, i: number) => (
                                                     <span key={i} className="px-2 py-1 border rounded bg-white shadow-sm">{renderReviewText(t)}</span>
                                                   ))}
                                                </div>
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="space-y-3">
                                              <div className="p-4 border rounded bg-gray-50">
                                                <div className="text-sm text-gray-600 mb-2">User Answer</div>
                                                <div className="text-gray-800">{userText ? renderReviewText(userText) : 'No answer'}</div>
                                              </div>
                                              {!aiScoredPrompt ? (
                                                <div className="p-4 border rounded bg-green-50">
                                                  <div className="text-sm text-gray-600 mb-2">Reference Answer</div>
                                                  <div className="text-green-700">{renderReviewText(qItem.options[0] || '')}</div>
                                                </div>
                                              ) : (
                                                <div className="space-y-3">
                                                  {awardedScore != null && (
                                                    <div className={`p-4 border rounded ${getHistoryScorePanelClass(scorePercentage)}`}>
                                                      <div className="flex items-center justify-between">
                                                        <div>
                                                          <div className={`text-sm font-bold ${getHistoryScoreTextClass(scorePercentage)}`}>AI Score</div>
                                                          <div className="text-xs text-gray-500">按 AI 百分比分数折算本题得分</div>
                                                        </div>
                                                        <div className="text-right">
                                                          <div className={`text-2xl font-bold ${getHistoryScoreTextClass(scorePercentage)}`}>{awardedScore}<span className="text-sm text-gray-400">/{maxScore}</span></div>
                                                          {aiScore != null && <div className={`text-xs ${getHistoryScoreTextClass(scorePercentage)}`}>AI: {aiScore}/100</div>}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}
                                                  {grading && (
                                                    <div className="p-4 border rounded bg-indigo-50 border-indigo-100 space-y-3">
                                                      <div className="flex items-center justify-between">
                                                        <div className={`text-sm font-bold ${getHistoryScoreTextClass(Number(grading.score || scorePercentage))}`}>AI 分析</div>
                                                        {typeof grading.score === 'number' && (
                                                          <div className={`text-2xl font-bold ${getHistoryScoreTextClass(grading.score)}`}>{grading.score}<span className="text-sm text-gray-400">/100</span></div>
                                                        )}
                                                      </div>
                                                      {grading.summary && <div className="text-sm text-gray-700">{grading.summary}</div>}
                                                      {Array.isArray(grading.dimensions) && grading.dimensions.length > 0 && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                          {grading.dimensions.map((d: any, i: number) => (
                                                            <div key={i} className="rounded border border-gray-200 bg-white p-2 text-sm">
                                                              <div className="flex justify-between">
                                                                <span className="font-semibold text-gray-700">{d.name}</span>
                                                                {typeof d.score === 'number' && <span className={`font-bold ${getHistoryScoreTextClass(d.score)}`}>{d.score}</span>}
                                                              </div>
                                                              {d.comment && <div className="mt-1 text-xs text-gray-500">{d.comment}</div>}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}
                                                      {Array.isArray(grading.corrections) && grading.corrections.length > 0 && (
                                                        <div className="space-y-2">
                                                          <div className="text-sm font-semibold text-gray-700">修改建议</div>
                                                          {grading.corrections.map((c: any, i: number) => (
                                                            <div key={i} className="border-l-2 border-amber-300 pl-2 text-sm">
                                                              <span className="text-red-500 line-through">{c.original}</span> → <span className="text-green-700">{c.suggestion}</span>
                                                              {c.reason && <div className="text-xs text-gray-500">{c.reason}</div>}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}
                                                      {grading.sample && (
                                                        <div className="rounded bg-green-50 p-3">
                                                          <div className="mb-1 text-sm font-semibold text-green-800">AI 参考范文</div>
                                                          <div className="text-sm text-gray-700 whitespace-pre-line">{grading.sample}</div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )
                                    ) : (
                                    <div className="space-y-3">
                                        {qItem.options.map((opt, oidx) => {
                                            const isChosen = ans === oidx && !isCscaPendingReview(qItem);
                                            const isRight = (qItem.correctAnswers || [qItem.correctAnswer]).includes(oidx);
                                            const isImage = opt && /\.(webp|jpg|png|jpeg)$/i.test(opt);
                                            
                                            let optStyle = "border-gray-100 bg-white";
                                            if (isRight) optStyle = "border-green-500 bg-green-50 ring-1 ring-green-500";
                                            else if (isChosen) optStyle = "border-red-500 bg-red-50 ring-1 ring-red-500";

                                            return (
                                                <div key={oidx} className={`p-4 border-2 rounded-xl transition-all ${optStyle}`}>
                                                    <div className="flex items-center gap-3">
                                                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isRight ? 'bg-green-200 text-green-800' : isChosen ? 'bg-red-200 text-red-800' : 'bg-gray-100 text-gray-500'}`}>
                                                            {String.fromCharCode(65 + oidx)}
                                                        </span>
                                                        {isImage ? (
                                                            <img 
                                                                src={`${API_BASE_URL}/media/${opt}`} 
                                                                alt={`Option`} 
                                                                className="h-32 w-auto object-contain rounded-lg border border-gray-100 bg-white"
                                                                onError={(e) => {
                                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyIDJDMTYuNDEgMiAyMCA1LjU5IDIwIDEwVjE0QzIwIDE4LjQxIDE2LjQxIDIyIDEyIDIyQzcuNTkgMjIgNCAxOC40MSA0IDE0VjEwQzQgNS41OSA3LjU5IDIgMTIgMloiIGZpbGw9IiNjY2MiLz48L3N2Zz4='; 
                                                                }}
                                                            />
                                                        ) : (
                                                            <span className="text-gray-800 font-medium">{renderReviewText(opt)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    )}
                                    {(qItem.explanation || isWrongViewDetail) && (
                                        <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl text-blue-800 text-sm">
                                            <span className="font-bold block mb-1">Explanation:</span>
                                            {reviewExplanation(qItem)
                                                ? renderReviewText(reviewExplanation(qItem))
                                                : <span className="italic text-blue-600/70">（暂无解析）</span>}
                                            {hasIndonesianExplanation && explanationLanguage === 'ID' && !qItem.explanationId && <div className="mt-2 text-xs">Terjemahan ID belum tersedia; pembahasan CN ditampilkan.</div>}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
          </div>
      );
  }

  const reviewPanel = filterVersion ? (
      <details open={reviewExpanded} onToggle={e => setReviewExpanded(e.currentTarget.open)} className="rounded-2xl border bg-white p-4"><summary className="cursor-pointer font-semibold text-indigo-800">回顾与错题练习</summary>
      {filterVersion !== 'CSCA' && <>
      {/* Average Stats Section */}
      {(availableStatsVersions.length > 0 || availableStatsLevels.length > 0) && (
        <div className="bg-white rounded-3xl border border-indigo-50 shadow-sm p-5 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-[#312E81]">Stats Filters</div>
              <div className="text-xs text-gray-500 mt-1">以下为当前页记录的分项表现。</div>
            </div>
            {recentLevelStats && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 text-[#4F46E5] text-sm font-bold">
                {formatHskTypeLabel(recentLevelStats.bankVersion)} {formatHskLevelLabel(recentLevelStats.level)}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">HSK Level</div>
              <div className="flex flex-wrap gap-2">
                {availableStatsLevels.map((level) => (
                  <button
                    key={`stats-level-${level}`}
                    onClick={() => setSelectedStatsLevel(level)}
                    className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all ${
                      selectedStatsLevel === level
                        ? 'bg-[#4F46E5] text-white shadow-sm'
                        : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {formatHskLevelLabel(level)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
            { id: 'listening', label: 'Listening', max: 100, icon: Volume2, color: 'text-blue-600', bg: 'bg-blue-50' },
            { id: 'reading', label: 'Reading', max: 100, icon: HelpCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { id: 'writing', label: 'Writing', max: 100, icon: CheckCircle, color: 'text-purple-600', bg: 'bg-purple-50' },
            { id: 'total', label: 'Composite Avg', max: 300, icon: Trophy, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(stat => {
            const avg = recentLevelStats ? Number(recentLevelStats[stat.id as keyof RecentLevelStats] || 0) : 0;
            const testsTaken = recentLevelStats?.totalTests || 0;
            const Icon = stat.icon;
            
            return (
                <div key={stat.id} className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col items-center justify-center gap-2">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${stat.bg} ${stat.color} mb-2`}>
                        <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-gray-500 font-bold text-sm uppercase tracking-wider">{stat.label}</div>
                    <div className="text-3xl font-bold text-gray-900">
                        {avg.toFixed(1)}<span className="text-sm text-gray-400 font-medium ml-1">/{stat.max}</span>
                    </div>
                    <div className="text-xs text-gray-400 font-medium">{testsTaken} records on this page</div>
                </div>
            );
        })}
      </div>

      </>}
      {/* Stats / Review Actions */}
      <div className="bg-white shadow-xl shadow-indigo-500/5 rounded-3xl p-6 border border-indigo-50">
          <h2 className="text-xl font-bold text-[#312E81] mb-6">Review & Practice</h2>
          {!wrongHistoryLoaded ? (
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-6 text-center">
              <p className="text-sm text-gray-600 mb-4">Wrong questions are loaded only when you need them, reducing unnecessary history reads.</p>
              {wrongHistoryError && <p className="text-sm text-red-600 mb-3">{wrongHistoryError}</p>}
              <button
                onClick={loadWrongHistory}
                disabled={wrongHistoryLoading}
                className="px-5 py-2.5 bg-[#4F46E5] text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {wrongHistoryLoading ? 'Loading...' : 'Load Wrong Question Practice'}
              </button>
            </div>
          ) : filterVersion === 'CSCA' ? (
            <CscaWrongPractice items={wrongList} allowedLevels={restrictions?.cscaLevels || []} onView={items => handleViewWrong('reading', items)} onCorrect={items => { const result = saveCorrectReviews(user?.id || '', items.map(item => item.wrongReviewSource)); setWrongList(previous => previous.filter(item => !isWrongResolved(item, result.progress))); return result.persisted; }} />
          ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {PRACTICE_TYPES.map(({ part, label }) => {
              const filters = practiceFilters[part];
              const filteredItems = filteredWrongByPractice[part] || [];
              const availableSubParts = availableSubPartsByPractice[part] || [];

              return (
                <div key={part} className="bg-gray-50 rounded-2xl p-4 border border-gray-100 hover:border-indigo-100 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="font-bold text-gray-700">{label}</div>
                      <div className="text-sm text-gray-500 mt-1">All wrong questions in this category.</div>
                    </div>
                    <div className="text-xs font-bold text-[#4F46E5] bg-indigo-50 px-2.5 py-1 rounded-full">
                      {filteredItems.length} Wrong
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">HSK Level</div>
                      <div className="flex flex-wrap gap-2">
                        {(availableLevelsByPractice[part] || []).map((level) => (
                          <button
                            key={`${part}-level-${level}`}
                            onClick={() => updatePracticeFilter(part, 'level', level)}
                            className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all ${
                              filters.level === level
                                ? 'bg-[#4F46E5] text-white shadow-sm'
                                : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {formatHskLevelLabel(level)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Question Type</div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          key={`${part}-subpart-all`}
                          onClick={() => updatePracticeFilter(part, 'subPart', 'all')}
                          className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all ${
                            filters.subPart === 'all'
                              ? 'bg-[#4F46E5] text-white shadow-sm'
                              : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          All
                        </button>
                        {availableSubParts.map((subPart) => (
                          <button
                            key={`${part}-subpart-${subPart}`}
                            onClick={() => updatePracticeFilter(part, 'subPart', subPart)}
                            className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all ${
                              filters.subPart === subPart
                                ? 'bg-[#4F46E5] text-white shadow-sm'
                                : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {subPart}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Review Count
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={reviewCounts[part]}
                        onChange={(e) => updateReviewCount(part, e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4F46E5]"
                      />
                    </div>

                    <button
                      onClick={() => handleViewWrong(part)}
                      disabled={filteredItems.length === 0}
                      className="w-full px-3 py-2 bg-white text-[#4F46E5] border border-indigo-200 rounded-xl hover:bg-indigo-50 text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      View Wrong ({filteredItems.length})
                    </button>

                    <button
                      onClick={() => startWrongPractice(part)}
                      disabled={filteredItems.length === 0}
                      className="w-full px-3 py-2 bg-[#4F46E5] text-white rounded-xl hover:bg-indigo-700 text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Review Wrong ({filteredItems.length})
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          )}
      </div>

      </details>
  ) : null;

  const scopePicker = restrictions && <><HistoryScope restrictions={restrictions} family={filterVersion} category={category} level={filterLevel} onChange={changeScope} beforeFilters={reviewPanel} />
    {filterVersion && filterVersion !== 'CSCA' && category === 'practice' && <div className="flex flex-wrap gap-2" aria-label="习题记录来源">{(filterVersion === '3.0' ? [['all','章节习题'],['workbook','早期习题记录'],['review','错题练习记录（本机）']] : [['all','习题记录'],['review','错题练习记录（本机）']]).map(([value,label]) => <button key={value} onClick={() => { setFilterSystem(value); setHistoryPage(1); }} aria-pressed={filterSystem === value} className={`rounded-lg border px-4 py-2 text-sm ${filterSystem === value ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'bg-white text-slate-600'}`}>{label}</button>)}</div>}
  </>;

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-[#312E81]">我的历史记录</h1>
        <p className="text-[#475569]">按学习体系整理答题记录，查看成绩与逐题回顾。</p>
      </div>

      {scopePicker}

      {loadingDetail && (
          <div className="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#4F46E5]"></div>
          </div>
      )}

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl flex flex-wrap items-center justify-between gap-3">
          <span>{error}</span>
          {restrictions && <button type="button" onClick={() => setHistoryRetry(value => value + 1)} className="rounded-lg border border-red-300 bg-white px-3 py-1.5 font-semibold hover:bg-red-100">重试</button>}
        </div>
      )}

      {filterVersion && (filterVersion === '3.0' && category === 'practice' && filterSystem === 'all' ? (
        token && <WorkbookHistoryList token={token} level={filterLevel} />
      ) : <>

      {/* History List */}
      <div className="space-y-4" aria-label="历史记录列表" aria-busy={loading}>
        <h2 className="text-xl font-bold text-[#312E81]">{historyCategoryLabel(isLocalReviewFilter ? category : shownHistory.category)}历史记录</h2>
        {error ? null : displayedHistoryTotal === 0 && filterVersion === 'all' && filterLevel === 'all' && filterSystem === 'all' && filterPart === 'all' ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm">
                <Trophy className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No tests completed yet</h3>
                <p className="text-gray-500 mb-6">Start taking tests to see your progress here.</p>
                <a href="/" className="px-6 py-3 bg-[#4F46E5] text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors">
                    Take a Test
                </a>
            </div>
        ) : (
            <div className="space-y-6">
                <HistoryRecordList records={historyItems} category={isLocalReviewFilter ? category : shownHistory.category} pending={loading} onResume={id => { const test = serverHistory.find(item => item.id === id); if (test) navigate(fullSimulationResumePath(test.level, id)); }} onReview={handleViewDetail} />
            {displayedHistoryTotal > 0 && (
              <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white px-5 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  Showing {((isLocalReviewFilter ? historyPage : shownHistory.page) - 1) * 5 + 1}-{Math.min((isLocalReviewFilter ? historyPage : shownHistory.page) * 5, displayedHistoryTotal)} of {displayedHistoryTotal} tests
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setHistoryPage((current) => Math.max(1, current - 1))}
                    disabled={loading || historyPage <= 1}
                    className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 font-bold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                  </button>
                  <span>Page {isLocalReviewFilter ? historyPage : shownHistory.page} of {displayedHistoryTotalPages}</span>
                  <button
                    onClick={() => setHistoryPage((current) => Math.min(displayedHistoryTotalPages, current + 1))}
                    disabled={loading || historyPage >= displayedHistoryTotalPages}
                    className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 font-bold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            </div>
        )}
      </div>
      </>)}
    </div>
  );
};

export default TestHistory;
